import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import FlagIcon from '../../components/FlagIcon';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';
import * as userActions from '../../store/actions/user';
import { buildCompanyOnboardingProfilePayload } from '../../utils/companyOnboardingStorage';
import {
  COMPANY_HIGH_RISK_ACTIVITY_OPTIONS,
  NONE_OF_THE_ABOVE_ACTIVITY,
  normalizeHighRiskActivities,
  serializeHighRiskActivities,
} from '../../utils/companyRiskActivities';
import { getResolvedNextStepPath, getResolvedPreviousStepPath, isKycOrLaterOnboardingStep } from '../../utils/onboardingUtils';

const fieldLabelClass = 'mb-2 block text-[12px] font-normal text-[#3D5A73]';
const errorClass = 'mt-1.5 text-xs text-[#C0392B] font-medium';
const textareaClass =
  'min-h-[92px] w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 py-3 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-[3px] focus:ring-[#2E6BAD]/[.12]';
const explanationClass = 'mt-3 mb-3.5 rounded-[12px] bg-[#EBF4FB] px-[18px] py-3.5 text-[13.5px] leading-[1.6] text-[#4E6F8A]';
const radioCardSelectedClass = 'border-[#1E4E80] shadow-[0_0_0_3px_rgba(30,78,128,0.08)]';
const radioCardIdleClass = 'border-[#DDE6EF] hover:border-[#B8C9D9]';

function booleanFromStored(value, defaultValue = null) {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return defaultValue;
}

function InfoTooltip({ text }) {
  return (
    <span className='group relative ml-1.5 inline-flex align-middle'>
      <button
        type='button'
        className='inline-flex h-[17px] w-[17px] cursor-help items-center justify-center rounded-full border border-[#B8C9D9] bg-[#F4F8FC] text-[11px] font-bold leading-none text-[#3D5A73] outline-none transition hover:border-[#1E4E80] hover:text-[#1E4E80] focus:border-[#1E4E80] focus:text-[#1E4E80] focus:ring-2 focus:ring-[#2E6BAD]/10'
      >
        i
      </button>
      <span className='pointer-events-none absolute right-0 top-full z-20 mt-2 hidden w-[min(280px,calc(100vw-48px))] rounded-[10px] bg-[#0B1929] px-3.5 py-3 text-left text-[12px] font-medium leading-5 text-white shadow-[0_12px_30px_rgba(11,25,41,0.18)] group-hover:block group-focus-within:block sm:left-1/2 sm:right-auto sm:w-[280px] sm:-translate-x-1/2'>
        {text}
      </span>
    </span>
  );
}

InfoTooltip.propTypes = {
  text: PropTypes.string.isRequired,
};

function CompanyRisk() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const setFooterConfig = useOnboardingFooter();
  const { user, onboardingStatus, upsertingCompanyOnboardingProfile, upsertCompanyOnboardingProfileError } = useSelector((state) => state.user);
  const prohibitedOperatingCountries = useSelector((state) => state.config?.prohibitedOperatingCountries ?? []);
  const prohibitedCountryList = useMemo(
    () => [...prohibitedOperatingCountries].sort((a, b) => String(a.name || a.code).localeCompare(String(b.name || b.code))),
    [prohibitedOperatingCountries],
  );
  const nextStepPath =
    getResolvedNextStepPath(location.pathname, onboardingStatus?.steps, { userType: user?.userType }) ?? '/accept-legal-agreements';
  const previousStepPath =
    getResolvedPreviousStepPath(location.pathname, onboardingStatus?.steps, { userType: user?.userType }) ?? '/financial';
  const existingProfile = useMemo(
    () => user?.company?.onboardingProfile ?? user?.company?.OnboardingProfile ?? {},
    [user?.company?.OnboardingProfile, user?.company?.onboardingProfile],
  );
  const [sanctioned, setSanctioned] = useState(null);
  const [sanctionsDetail, setSanctionsDetail] = useState('');
  const [accountRestricted, setAccountRestricted] = useState(null);
  const [accountRestrictedDetail, setAccountRestrictedDetail] = useState('');
  const [operatesInProhibitedCountries, setOperatesInProhibitedCountries] = useState(null);
  const [highRiskActivities, setHighRiskActivities] = useState([NONE_OF_THE_ABOVE_ACTIVITY]);
  const [conductsMoneyServices, setConductsMoneyServices] = useState(null);
  const [conductsMoneyServicesDescription, setConductsMoneyServicesDescription] = useState('');
  const [actingAsIntermediary, setActingAsIntermediary] = useState(null);
  const [errors, setErrors] = useState({});
  const [pendingBackendProfile, setPendingBackendProfile] = useState(null);
  const [hasSeenBackendSaving, setHasSeenBackendSaving] = useState(false);
  const isKycOrLaterStep = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);

  useEffect(() => {
    const profile = existingProfile;
    setSanctioned(booleanFromStored(profile.isSanctioned ?? profile.IsSanctioned));
    setSanctionsDetail(profile.sanctionsDetail ?? profile.SanctionsDetail ?? '');
    setAccountRestricted(booleanFromStored(profile.hasAccountRestricted ?? profile.HasAccountRestricted));
    setAccountRestrictedDetail(profile.accountRestrictedDetail ?? profile.AccountRestrictedDetail ?? '');
    setOperatesInProhibitedCountries(
      booleanFromStored(profile.operatesInProhibitedCountries ?? profile.OperatesInProhibitedCountries),
    );
    const storedActivities = normalizeHighRiskActivities(profile.highRiskActivities ?? profile.HighRiskActivities);
    setHighRiskActivities(storedActivities.length > 0 ? storedActivities : [NONE_OF_THE_ABOVE_ACTIVITY]);
    setConductsMoneyServices(booleanFromStored(profile.conductsMoneyServices ?? profile.ConductsMoneyServices));
    setConductsMoneyServicesDescription(
      profile.conductsMoneyServicesDescription ?? profile.ConductsMoneyServicesDescription ?? '',
    );
    setActingAsIntermediary(booleanFromStored(profile.actingAsIntermediary ?? profile.ActingAsIntermediary));
  }, [existingProfile]);

  const toggleActivity = useCallback((value) => {
    setHighRiskActivities((prev) => {
      if (value === NONE_OF_THE_ABOVE_ACTIVITY) {
        return prev.includes(NONE_OF_THE_ABOVE_ACTIVITY) ? [] : [NONE_OF_THE_ABOVE_ACTIVITY];
      }
      const withoutNone = prev.filter((item) => item !== NONE_OF_THE_ABOVE_ACTIVITY);
      if (withoutNone.includes(value)) {
        return withoutNone.filter((item) => item !== value);
      }
      return [...withoutNone, value];
    });
    setErrors((prev) => {
      if (!prev.highRiskActivities) return prev;
      const { highRiskActivities: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const validate = useCallback(() => {
    const nextErrors = {};
    if (typeof sanctioned !== 'boolean') nextErrors.sanctioned = 'Please answer this question.';
    if (sanctioned === true && !sanctionsDetail.trim()) nextErrors.sanctionsDetail = 'Please describe the connection.';
    if (typeof accountRestricted !== 'boolean') nextErrors.accountRestricted = 'Please answer this question.';
    if (accountRestricted === true && !accountRestrictedDetail.trim()) {
      nextErrors.accountRestrictedDetail = 'Please provide details.';
    }
    if (typeof operatesInProhibitedCountries !== 'boolean') {
      nextErrors.operatesInProhibitedCountries = 'Please answer this question.';
    }
    if (highRiskActivities.length === 0) {
      nextErrors.highRiskActivities = 'Select at least one option.';
    }
    if (typeof conductsMoneyServices !== 'boolean') nextErrors.conductsMoneyServices = 'Please answer this question.';
    if (conductsMoneyServices === true && !conductsMoneyServicesDescription.trim()) {
      nextErrors.conductsMoneyServicesDescription = 'Please describe your money services activities.';
    }
    if (typeof actingAsIntermediary !== 'boolean') nextErrors.actingAsIntermediary = 'Please answer this question.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [
    accountRestricted,
    accountRestrictedDetail,
    actingAsIntermediary,
    conductsMoneyServices,
    conductsMoneyServicesDescription,
    highRiskActivities,
    operatesInProhibitedCountries,
    sanctioned,
    sanctionsDetail,
  ]);

  const handleContinue = useCallback(() => {
    if (isKycOrLaterStep) {
      if (nextStepPath) navigate(nextStepPath);
      return;
    }

    if (!validate()) return;

    const profile = buildCompanyOnboardingProfilePayload({
      ...existingProfile,
      isSanctioned: sanctioned,
      sanctionsDetail: sanctioned ? sanctionsDetail.trim() : null,
      hasAccountRestricted: accountRestricted,
      accountRestrictedDetail: accountRestricted ? accountRestrictedDetail.trim() : null,
      operatesInProhibitedCountries,
      highRiskActivities: serializeHighRiskActivities(highRiskActivities),
      conductsMoneyServices,
      conductsMoneyServicesDescription: conductsMoneyServices ? conductsMoneyServicesDescription.trim() : null,
      actingAsIntermediary,
    });

    setPendingBackendProfile(profile);
    setHasSeenBackendSaving(false);
    dispatch(userActions.upsertCompanyOnboardingProfile(profile));
  }, [
    accountRestricted,
    accountRestrictedDetail,
    actingAsIntermediary,
    conductsMoneyServices,
    conductsMoneyServicesDescription,
    dispatch,
    existingProfile,
    highRiskActivities,
    isKycOrLaterStep,
    navigate,
    nextStepPath,
    operatesInProhibitedCountries,
    sanctioned,
    sanctionsDetail,
    validate,
  ]);

  const handleBack = useCallback(() => {
    if (previousStepPath) navigate(previousStepPath);
  }, [navigate, previousStepPath]);

  useEffect(() => {
    setFooterConfig({
      continueLabel: upsertingCompanyOnboardingProfile ? 'Saving...' : 'Continue',
      continueDisabled: upsertingCompanyOnboardingProfile,
      onContinue: handleContinue,
      backDisabled: upsertingCompanyOnboardingProfile,
      onBack: handleBack,
      phaseLabel: 'Activity',
    });
  }, [handleBack, handleContinue, setFooterConfig, upsertingCompanyOnboardingProfile]);

  useEffect(() => () => setFooterConfig({}), [setFooterConfig]);

  useEffect(() => {
    if (pendingBackendProfile && upsertingCompanyOnboardingProfile) {
      setHasSeenBackendSaving(true);
    }
  }, [pendingBackendProfile, upsertingCompanyOnboardingProfile]);

  useEffect(() => {
    if (pendingBackendProfile && hasSeenBackendSaving && !upsertingCompanyOnboardingProfile && !upsertCompanyOnboardingProfileError) {
      setPendingBackendProfile(null);
      setHasSeenBackendSaving(false);
      navigate(nextStepPath);
    }
  }, [
    hasSeenBackendSaving,
    navigate,
    nextStepPath,
    pendingBackendProfile,
    upsertCompanyOnboardingProfileError,
    upsertingCompanyOnboardingProfile,
  ]);

  const renderBooleanCards = (value, onChange) => (
    <div className='mt-4 grid grid-cols-2 gap-2'>
      {[
        { value: false, label: 'No' },
        { value: true, label: 'Yes' },
      ].map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.label}
            type='button'
            onClick={() => onChange(option.value)}
            className={`rounded-[12px] border bg-white px-4 py-3 text-left text-sm font-normal transition ${
              isSelected ? radioCardSelectedClass : radioCardIdleClass
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  const highRiskOptions = COMPANY_HIGH_RISK_ACTIVITY_OPTIONS.filter((o) => o.kind === 'highRisk');
  const prohibitedOptions = COMPANY_HIGH_RISK_ACTIVITY_OPTIONS.filter((o) => o.kind === 'prohibited');
  const noneOption = COMPANY_HIGH_RISK_ACTIVITY_OPTIONS.find((o) => o.kind === 'none');

  const renderActivityChip = (option) => {
    const isSelected = highRiskActivities.includes(option.value);
    return (
      <button
        key={option.value}
        type='button'
        onClick={() => toggleActivity(option.value)}
        aria-pressed={isSelected}
        className={`rounded-full border px-4 py-2 text-left text-[12.5px] font-medium leading-5 transition ${
          isSelected
            ? 'border-[#8DBDE6] bg-[#EBF4FB] text-[#1E4E80]'
            : 'border-[#DDE6EF] bg-white text-[#0B1929] hover:border-[#B8C9D9]'
        }`}
      >
        {option.label}
      </button>
    );
  };

  return (
    <div className='flex w-full justify-center bg-[#EBF4FB] px-4 pt-7 font-sans text-[#0B1929] sm:min-h-screen sm:px-6'>
      <div className='w-full max-w-[540px]'>
        <div className='rounded-[20px] bg-white px-6 py-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:mb-24 sm:px-10 sm:py-9'>
          <fieldset disabled={isKycOrLaterStep} className='block border-0 p-0'>
            <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
              <svg width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
              </svg>
            </div>

            <h1 className='mb-2.5 text-center font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Risk Declarations.</h1>
            <p className='mt-2 text-center text-sm leading-6 text-[#4E6F8A]'>
              Required for compliance and our banking partners. Your answers are confidential.
            </p>

            <div className='mt-7 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='text-[12px] font-normal leading-5 text-[#3D5A73]'>
                Does your company, including its directors, UBOs, shareholders, business partners, accounts, source of funds, or planned
                transactions, have any connection to sanctioned, embargoed, or high-risk countries, persons, or entities?
                <InfoTooltip text='This covers any direct or indirect link between your company, group, directors, UBOs, suppliers, customers, or payment counterparties and parties or territories on the EU, UN, US (OFAC), or UK sanctions lists — including Russia, Iran, North Korea, Belarus, and Syria.' />
              </div>
              {renderBooleanCards(sanctioned, setSanctioned)}
              {errors.sanctioned && <p className={errorClass}>{errors.sanctioned}</p>}
              {sanctioned === true && (
                <>
                  <div className={explanationClass}>
                    Please describe the connection, including the country, person/entity involved, nature of the relationship, and whether funds,
                    goods, services, or transactions are involved.
                  </div>
                  <div>
                    <label htmlFor='company-sanctionsDetail' className={fieldLabelClass}>
                      Please describe the connection
                    </label>
                    <textarea
                      id='company-sanctionsDetail'
                      value={sanctionsDetail}
                      onChange={(event) => setSanctionsDetail(event.target.value)}
                      placeholder='Include the country, person/entity involved, nature of the relationship, and whether funds, goods, services, or transactions are involved.'
                      className={textareaClass}
                      rows={3}
                    />
                    {errors.sanctionsDetail && <p className={errorClass}>{errors.sanctionsDetail}</p>}
                  </div>
                </>
              )}
            </div>

            <div className='mt-5 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='text-[12px] font-normal leading-5 text-[#3D5A73]'>
                Has your company ever had its account restricted or terminated by a bank or financial service provider?
                <InfoTooltip text='This includes any situation where your company, a group company, director, UBO, or related account had services restricted, frozen, terminated, placed on reserve, or subjected to enhanced monitoring by a bank, acquirer, PSP, EMI, or payment or MICA partner.' />
              </div>
              {renderBooleanCards(accountRestricted, setAccountRestricted)}
              {errors.accountRestricted && <p className={errorClass}>{errors.accountRestricted}</p>}
              {accountRestricted === true && (
                <>
                  <div className={explanationClass}>
                    Please describe what happened, including the name of the provider, the reason given, the date, and the current status of the
                    account or relationship.
                  </div>
                  <div>
                    <label htmlFor='company-accountRestrictedDetail' className={fieldLabelClass}>
                      Please provide details
                    </label>
                    <textarea
                      id='company-accountRestrictedDetail'
                      value={accountRestrictedDetail}
                      onChange={(event) => setAccountRestrictedDetail(event.target.value)}
                      placeholder='Include the provider, reason, date, and current status.'
                      className={textareaClass}
                      rows={3}
                    />
                    {errors.accountRestrictedDetail && <p className={errorClass}>{errors.accountRestrictedDetail}</p>}
                  </div>
                </>
              )}
            </div>

            <div className='mt-5 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='text-[12px] font-normal leading-5 text-[#3D5A73]'>
                Does your company operate in any prohibited countries or regions?
                <InfoTooltip text='Answer Yes if the company has operations, customers, suppliers, directors, UBOs, or payment flows in any country listed below. We cannot serve these countries.' />
              </div>
              {prohibitedCountryList.length > 0 && (
                <div className='mt-3 flex flex-wrap gap-2'>
                  {prohibitedCountryList.map((country) => (
                    <span
                      key={country.code}
                      className='inline-flex items-center gap-1.5 rounded-full border border-[#DDE6EF] bg-[#F4F8FC] px-3 py-1.5 text-[12.5px] font-medium text-[#0B1929]'
                    >
                      <FlagIcon countryCode={country.code} className='h-3.5 w-[18px] rounded-[2px]' />
                      {country.name || country.code}
                    </span>
                  ))}
                </div>
              )}
              {renderBooleanCards(operatesInProhibitedCountries, setOperatesInProhibitedCountries)}
              {errors.operatesInProhibitedCountries && <p className={errorClass}>{errors.operatesInProhibitedCountries}</p>}
            </div>

            <div className='mt-5 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='text-[12px] font-normal leading-5 text-[#3D5A73]'>
                Does your company engage in any of the activities listed below?
                <InfoTooltip text='High-risk activities are disclosed to our banking partner for case-by-case review.' />
              </div>
              <p className='mt-1.5 text-[12px] leading-5 text-[#7A9AB0]'>Tap all that apply, even if it&apos;s a small share.</p>

              <div className='mt-4 flex flex-wrap gap-3'>
                {noneOption ? renderActivityChip(noneOption) : null}
                {highRiskOptions.map(renderActivityChip)}
                {prohibitedOptions.map(renderActivityChip)}
              </div>

              {errors.highRiskActivities && <p className={errorClass}>{errors.highRiskActivities}</p>}
            </div>

            <div className='mt-5 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='text-[12px] font-normal leading-5 text-[#3D5A73]'>
                Does your company offer money services, investment products, and/or other financial services?
                <InfoTooltip text='Includes remittance, forex, payment processing, investment products, or similar regulated financial services.' />
              </div>
              {renderBooleanCards(conductsMoneyServices, setConductsMoneyServices)}
              {errors.conductsMoneyServices && <p className={errorClass}>{errors.conductsMoneyServices}</p>}
              {conductsMoneyServices === true && (
                <div className='mt-3'>
                  <label htmlFor='company-conductsMoneyServicesDescription' className={fieldLabelClass}>
                    Describe these activities
                  </label>
                  <textarea
                    id='company-conductsMoneyServicesDescription'
                    value={conductsMoneyServicesDescription}
                    onChange={(event) => setConductsMoneyServicesDescription(event.target.value)}
                    placeholder='Types of services, markets, volumes, licensing, and key partners.'
                    className={textareaClass}
                    rows={3}
                  />
                  {errors.conductsMoneyServicesDescription && (
                    <p className={errorClass}>{errors.conductsMoneyServicesDescription}</p>
                  )}
                </div>
              )}
            </div>

            <div className='mt-5 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='text-[12px] font-normal leading-5 text-[#3D5A73]'>
                Is your company acting as an intermediary by moving funds on behalf of others?
                <InfoTooltip text='Answer Yes if your company moves, holds, or settles funds for third parties rather than only for its own business activity.' />
              </div>
              {renderBooleanCards(actingAsIntermediary, setActingAsIntermediary)}
              {errors.actingAsIntermediary && <p className={errorClass}>{errors.actingAsIntermediary}</p>}
            </div>

            {upsertCompanyOnboardingProfileError && (
              <div className='mt-5 rounded-[12px] border border-[#F4C7C3] bg-[#FFF7F6] px-4 py-3 text-sm font-medium text-[#A83227]'>
                {upsertCompanyOnboardingProfileError?.message ??
                  upsertCompanyOnboardingProfileError?.detail ??
                  'Could not save your answers. Please try again.'}
              </div>
            )}
          </fieldset>
        </div>
      </div>
    </div>
  );
}

export default CompanyRisk;
